import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@os/components/ui/dialog";
import { Button } from "@os/components/ui/button";
import { Input } from "@os/components/ui/input";
import { EXCEL_FUNCTIONS, type ExcelFunctionMeta } from "@os/utils/excelFunctions";
import { Search, Calculator } from "lucide-react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert: (fnName: string) => void;
};

const RECENTS_KEY = "maplesheet_recent_functions";

const FormulaLibraryDialog: React.FC<Props> = ({ open, onOpenChange, onInsert }) => {
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [recents, setRecents] = useState<string[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      if (raw) setRecents(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, [open]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    EXCEL_FUNCTIONS.forEach(f => set.add(f.category || "Other"));
    return ["All", "Recently Used", ...Array.from(set).sort()];
  }, []);

  const recentFunctions: ExcelFunctionMeta[] = useMemo(() => {
    if (recents.length === 0) return [];
    const map = new Map(EXCEL_FUNCTIONS.map(f => [f.name, f]));
    return recents.map(n => map.get(n)).filter(Boolean) as ExcelFunctionMeta[];
  }, [recents]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let src: ExcelFunctionMeta[] = EXCEL_FUNCTIONS;
    if (selectedCategory === "Recently Used") {
      src = recentFunctions;
    } else if (selectedCategory !== "All") {
      src = EXCEL_FUNCTIONS.filter(f => (f.category || "Other") === selectedCategory);
    }
    const out = q.length
      ? src.filter(f =>
          f.name.toLowerCase().includes(q) ||
          (f.signature?.toLowerCase().includes(q) ?? false)
        )
      : src.slice();
    return out.slice(0, 200);
  }, [query, selectedCategory, recentFunctions]);

  const selected = filtered[selectedIndex];

  const handleInsert = (fnName: string, closeAfter = true) => {
    try {
      const next = [fnName, ...recents.filter(n => n !== fnName)].slice(0, 12);
      setRecents(next);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
    onInsert(fnName);
    if (closeAfter) onOpenChange(false);
  };

  const quickActions = ["SUM", "AVERAGE", "COUNT", "MIN", "MAX"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary" /> Function Library
          </DialogTitle>
          <DialogDescription>
            Browse functions by category, search, then insert into your formula. After inserting, hold Shift and select a range (e.g., A4:A8).
          </DialogDescription>
        </DialogHeader>

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2">
          {quickActions.map(name => (
            <Button
              key={name}
              size="sm"
              variant="secondary"
              onClick={() => handleInsert(name)}
              title={`Insert ${name}()`}
            >
              {name}
            </Button>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-12 gap-4">
          {/* Categories */}
          <div className="col-span-3">
            <div className="text-xs font-medium mb-2 text-muted-foreground">Categories</div>
            <div className="space-y-1 max-h-[320px] overflow-auto pr-1">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => { setSelectedCategory(cat); setSelectedIndex(0); }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-muted ${
                    selectedCategory === cat ? "bg-muted font-medium" : ""
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* List + Search */}
          <div className="col-span-5">
            <div className="relative mb-2">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Search functions (e.g., sum, vlookup)…"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
              />
            </div>
            <div className="border rounded-md h-[320px] overflow-auto">
              <ul>
                {filtered.map((f, idx) => (
                  <li
                    key={`${f.name}-${idx}`}
                    onClick={() => setSelectedIndex(idx)}
                    onDoubleClick={() => handleInsert(f.name)}
                    className={`px-3 py-2 cursor-pointer text-sm flex items-start gap-2 border-b last:border-b-0 border-border ${
                      idx === selectedIndex ? "bg-muted" : "hover:bg-muted/60"
                    }`}
                  >
                    <span className="font-medium text-foreground">{f.name}</span>
                    {f.signature ? (
                      <span className="text-muted-foreground truncate">{f.signature}</span>
                    ) : null}
                  </li>
                ))}
                {filtered.length === 0 && (
                  <li className="px-3 py-10 text-sm text-muted-foreground text-center">No functions found.</li>
                )}
              </ul>
            </div>
          </div>

          {/* Details + Actions */}
          <div className="col-span-4 flex flex-col">
            <div className="text-xs font-medium mb-2 text-muted-foreground">Details</div>
            <div className="flex-1 border rounded-md p-3 text-sm space-y-2">
              {selected ? (
                <>
                  <div className="font-semibold">{selected.name}</div>
                  {selected.signature ? (
                    <div className="text-muted-foreground">{selected.signature}</div>
                  ) : (
                    <div className="text-muted-foreground">No signature available.</div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    Category: {selected.category || "Other"}
                  </div>
                </>
              ) : (
                <div className="text-muted-foreground">Select a function to see details.</div>
              )}
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              <Button disabled={!selected} onClick={() => selected && handleInsert(selected.name, false)}>
                Insert
              </Button>
              <Button disabled={!selected} onClick={() => selected && handleInsert(selected.name, true)}>
                Insert & Close
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FormulaLibraryDialog;